import { FilterValues, ResolvedFilter } from '../types/insights.types'

// Translates between raw adapter values (the URL adapter stores everything as
// `string | string[]`) and the typed shapes the API and UI expect, driven by
// each filter's declared `valueType`.

const asArray = (value: string | string[] | undefined): string[] =>
  value === undefined
    ? []
    : Array.isArray(value)
    ? value.filter(Boolean)
    : [value].filter(Boolean)

const asScalar = (value: string | string[] | undefined): string | undefined =>
  Array.isArray(value) ? value[0] : value

/**
 * Coerces raw adapter values into typed {@link FilterValues} per the filter
 * definitions: array filters become arrays, scalar filters become single values.
 * Keys absent from `defs` are dropped, so unknown query params never reach the API.
 */
export const deserialiseFilters = (
  defs: ResolvedFilter[],
  raw: FilterValues
): FilterValues => {
  const out: FilterValues = {}

  for (const def of defs) {
    // A dateRange filter owns the `period` xor `from`/`to` wire keys (not its own
    // `def.key`), so read those directly. period wins if somehow both are present.
    if (def.valueType === 'dateRange') {
      const period = asScalar(raw.period)

      if (period) {
        out.period = period
        continue
      }

      const from = asScalar(raw.from)
      const to = asScalar(raw.to)

      if (from && to) {
        out.from = from
        out.to = to
      }
      continue
    }

    const value = raw[def.key]

    if (value === undefined || value === null) continue

    switch (def.valueType) {
      case 'stringArray':
      case 'enumArray': {
        const arr = asArray(value)

        if (arr.length > 0) out[def.key] = arr
        break
      }
      case 'enum':
      case 'isoDatetime': {
        const scalar = asScalar(value)

        if (scalar) out[def.key] = scalar
        break
      }
    }
  }

  return out
}

/**
 * Flattens {@link FilterValues} into the params object passed to the API client.
 * Drops `undefined` and empty arrays so the request only carries active filters —
 * matching how the hand-crafted services omitted unset params.
 */
export const serialiseFiltersForApi = (
  values: FilterValues
): Record<string, string | string[]> => {
  const params: Record<string, string | string[]> = {}

  for (const [key, value] of Object.entries(values)) {
    if (value === undefined) continue

    if (Array.isArray(value)) {
      if (value.length > 0) params[key] = value
    } else {
      params[key] = value
    }
  }

  return params
}

/** True when any filter value is set (non-empty), used to drive a "reset" affordance. */
export const hasActiveValues = (values: FilterValues): boolean =>
  Object.values(values).some((value) =>
    Array.isArray(value)
      ? value.length > 0
      : value !== undefined && value !== ''
  )
